"use client";
import React, { useState } from "react";
import { MobileHeader } from "../MobileHeader";
import { TabBar } from "../TabBar";
import MetricCard from "../MetricCard";
import { Link, useNavigate } from "react-router-dom";
import UsagePieChart from "../UsagePieChart";
import ToastNotification from "../ToastNotification";

interface UsageDataPoint {
  day: string;
  value: number;
}

const UsageDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState<{type: 'success' | 'info' | 'warning' | 'error', message: string} | null>(null);
  const [activePeriod, setActivePeriod] = useState<'day' | 'week' | 'month'>('week');
  
  // Sample data for the usage chart
  const weeklyData: UsageDataPoint[] = [
    { day: "Mon", value: 28 },
    { day: "Tue", value: 32 },
    { day: "Wed", value: 25 },
    { day: "Thu", value: 29 },
    { day: "Fri", value: 22 },
    { day: "Sat", value: 18 },
    { day: "Sun", value: 27 },
  ];
  
  // Calculate average usage
  const averageUsage = Math.round(
    weeklyData.reduce((sum, day) => sum + day.value, 0) / weeklyData.length
  );
  
  // Calculate total weekly usage
  const totalWeeklyUsage = weeklyData.reduce((sum, day) => sum + day.value, 0);
  
  return (
    <main className="pb-72 mx-auto w-full max-w-[480px]">
      <div className="overflow-hidden w-full bg-white">
        <MobileHeader />
        <section className="pt-8 pb-12 w-full">
          <header className="w-full text-2xl font-semibold leading-none text-gray-900">
            <div className="px-4 w-full">
              <div className="w-full">
                <div className="flex flex-col justify-center w-full">
                  <h1 className="w-full">
                    Water Usage
                  </h1>
                </div>
              </div>
            </div>
          </header>

          <section className="mt-8 w-full">
            <div className="px-4 w-full">
              {/* Period selector */}
              <div className="flex gap-2 justify-center w-full bg-gray-100 p-1 rounded-xl">
                <button 
                  onClick={() => setActivePeriod('day')} 
                  className={`py-2 px-4 rounded-lg text-sm font-medium flex-1 ${activePeriod === 'day' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}
                >
                  Day
                </button>
                <button 
                  onClick={() => setActivePeriod('week')} 
                  className={`py-2 px-4 rounded-lg text-sm font-medium flex-1 ${activePeriod === 'week' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}
                >
                  Week
                </button>
                <button 
                  onClick={() => setActivePeriod('month')} 
                  className={`py-2 px-4 rounded-lg text-sm font-medium flex-1 ${activePeriod === 'month' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}
                >
                  Month
                </button>
              </div>

              {/* Usage Metrics */}
              <div className="mt-6 grid grid-cols-2 gap-4">
                <MetricCard
                  title="Average Daily"
                  value={`${averageUsage}L`}
                  changePercentage="7%"
                  changeDirection="down"
                  comparisonText="vs last week"
                  className=""
                />
                
                <MetricCard
                  title="Total (Week)"
                  value={`${totalWeeklyUsage}L`}
                  changePercentage="5%"
                  changeDirection="down"
                  comparisonText="vs last week"
                  className=""
                />
              </div>

              {/* Usage Chart */}
              <div className="mt-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <h2 className="text-base font-semibold text-gray-900">Usage Breakdown</h2>
                
                <div className="mt-4 h-60">
                  <div className="relative h-full">
                    {/* Y axis lines */}
                    <div className="absolute top-0 left-0 w-full h-full flex flex-col justify-between">
                      <div className="border-t border-gray-100 w-full"></div>
                      <div className="border-t border-gray-100 w-full"></div>
                      <div className="border-t border-gray-100 w-full"></div>
                      <div className="border-t border-gray-100 w-full"></div>
                      <div className="border-t border-gray-100 w-full"></div>
                    </div>
                    
                    {/* Bars */}
                    <div className="relative h-full flex justify-between pt-5 pb-10">
                      {weeklyData.map((data, index) => {
                        const heightPercentage = (data.value / 40) * 100; // 40L as max for scaling
                        
                        return (
                          <div key={index} className="flex flex-col items-center justify-end flex-1">
                            <div 
                              className="w-8 bg-blue-500 rounded-t-md"
                              style={{ height: `${heightPercentage}%` }}
                            ></div>
                            <div className="mt-2 text-xs text-gray-500">{data.day}</div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Y axis labels */}
                    <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-400 py-1">
                      <span>40L</span>
                      <span>30L</span>
                      <span>20L</span>
                      <span>10L</span>
                      <span>0L</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Usage Breakdown */}
              <div className="mt-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Usage by Category</h2>
                
                <UsagePieChart 
                  data={[
                    { category: 'Shower & Bath', percentage: 38, color: '#2563EB', liters: 76 },
                    { category: 'Kitchen', percentage: 24, color: '#10B981', liters: 48 },
                    { category: 'Laundry', percentage: 20, color: '#8B5CF6', liters: 40 },
                    { category: 'Toilet', percentage: 15, color: '#F97316', liters: 30 },
                    { category: 'Other', percentage: 3, color: '#6B7280', liters: 6 }
                  ]}
                  showCard={false}
                />
              </div>

              {/* Usage Insights Notification - Made clickable */}
              <div 
                className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => {
                  navigate('/conservation-tips');
                  setToast({ type: 'info', message: 'Viewing water conservation tips' });
                }}
              >
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 5V10M10 15H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#1E40AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-blue-800">Usage Insight</h3>
                    <p className="mt-1 text-sm text-blue-700">Your shower time has decreased by 15% this week, saving approximately 10L per day. Keep up the good work!</p>
                  </div>
                  <div className="flex-shrink-0 ml-auto">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 12L10 8L6 4" stroke="#1E40AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </section>
      </div>

      <TabBar />
      
      {/* Toast notification */}
      {toast && (
        <ToastNotification
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </main>
  );
};

export default UsageDashboard;
